import React, { useState, useEffect, useCallback } from 'react';
import Navbar from '../components/Navbar';
import OrderSelectorModal from '../components/OrderSelectorModal';
import ProductSelectorModal from '../components/ProductSelectorModal';
import SupplierSelectorModal from '../components/SupplierSelectorModal';
import QuotationFormModal from '../components/QuotationFormModal';
import { formatCurrency, formatDate } from '../utils/formatters';
import './GestaoCotacoes.css';

const GestaoCotacoes = () => {
    const [quotations, setQuotations] = useState([]);
    const [selectedCot, setSelectedCot] = useState(null);
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isOrderSelectorOpen, setIsOrderSelectorOpen] = useState(false);
    const [pendingItemForOrder, setPendingItemForOrder] = useState(null);
    const [isLinkingQuotation, setIsLinkingQuotation] = useState(false);
    const [selectedItems, setSelectedItems] = useState([]);
    const [isProductSelectorOpen, setIsProductSelectorOpen] = useState(false);
    const [pendingItemForSync, setPendingItemForSync] = useState(null);
    const [isSupplierSelectorOpen, setIsSupplierSelectorOpen] = useState(false);
    const [selectedSupplierForNew, setSelectedSupplierForNew] = useState(null);

    const fetchUser = useCallback(async () => {
        const res = await fetch('/api/auth/me', { credentials: 'include' });
        if (res.ok) {
            const data = await res.json();
            setUser(data.user);
        }
    }, []);

    const fetchQuotations = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch('/api/cotacoes');
            if (res.ok) setQuotations(await res.json());
        } catch (error) {
            console.error('Erro ao buscar cotações:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchDetails = useCallback(async (id) => {
        try {
            const res = await fetch(`/api/cotacoes/${id}`);
            if (res.ok) {
                const data = await res.json();
                setSelectedCot(data);
            }
        } catch (error) {
            console.error('Erro ao buscar detalhes:', error);
        }
    }, []);

    useEffect(() => {
        fetchUser();
        fetchQuotations();
    }, [fetchUser, fetchQuotations]);

    const handleConvertToProduct = async (item) => {
        const option = window.confirm(`Deseja ATUALIZAR um produto existente para "${item.NomeProduto}"?\n(Clique em OK para ATUALIZAR, Cancelar para CADASTRAR NOVO)`);

        let targetProductId = null;
        if (option) {
            const idInput = prompt(`Digite o ID do produto para atualizar "${item.NomeProduto}":`, item.IDProduto || '');
            if (!idInput) return;
            targetProductId = parseInt(idInput);
        }

        const precoVenda = prompt(`Qual o preço de venda para ${item.NomeProduto}? (Custo: ${item.PrecoOfertado})`, item.PrecoOfertado);
        if (precoVenda === null) return;

        try {
            const res = await fetch('/api/cotacoes/converter', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    idCotacao: selectedCot.ID,
                    idItem: item.ID,
                    PrecoVenda: precoVenda,
                    targetProductId: targetProductId
                }),
                credentials: 'include'
            });
            if (res.ok) {
                alert('Operação concluída com sucesso!');
                fetchDetails(selectedCot.ID);
            }
        } catch (error) {
            console.error('Erro ao converter:', error);
        }
    };

    const handleSendToOrder = (item = null) => {
        if (item) {
            setPendingItemForOrder({ id: item.ID, isBulk: false });
        } else {
            // Bulk mode
            if (selectedItems.length === 0) return;
            setPendingItemForOrder({ id: 'bulk', isBulk: true });
        }
        setIsLinkingQuotation(true);
        setIsOrderSelectorOpen(true);
    };

    const confirmSendToOrder = async (order) => {
        try {
            const idsToSend = pendingItemForOrder?.isBulk ? selectedItems : [pendingItemForOrder.id];

            const res = await fetch('/api/cotacoes/enviar-pedido', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    IDCotacao: selectedCot.ID,
                    idsItens: idsToSend,
                    IDPedidoExistente: order.ID
                }),
                credentials: 'include'
            });
            if (res.ok) {
                const data = await res.json();
                alert(data.message);
                setIsOrderSelectorOpen(false);
                setPendingItemForOrder(null);
                setSelectedItems([]); // Clear selection after bulk
            } else {
                const data = await res.json();
                alert(`Erro ao vincular: ${data.message || 'Erro desconhecido'}`);
            }
        } catch (error) {
            console.error('Erro ao enviar pedido:', error);
            alert('Falha na comunicação com o servidor ao vincular itens.');
        }
    };

    const handleSyncClick = (item) => {
        setPendingItemForSync(item);
        setIsProductSelectorOpen(true);
    };

    const confirmSync = async (product) => {
        const precoVenda = prompt(`Sincronizar "${pendingItemForSync.NomeProduto}" com "${product.NomeProduto}"?\nQual o preço de venda? (Custo: ${pendingItemForSync.PrecoOfertado})`, pendingItemForSync.PrecoOfertado);
        if (precoVenda === null) return;

        try {
            const res = await fetch('/api/cotacoes/converter', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    idCotacao: selectedCot.ID,
                    idItem: pendingItemForSync.ID,
                    PrecoVenda: precoVenda,
                    targetProductId: product.ID
                }),
                credentials: 'include'
            });
            if (res.ok) {
                alert('Produto sincronizado com sucesso!');
                setIsProductSelectorOpen(false);
                fetchDetails(selectedCot.ID);
            }
        } catch (error) {
            console.error('Erro ao sincronizar:', error);
        }
    };

    const toggleItemSelection = (id) => {
        setSelectedItems(prev =>
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
    };

    const toggleAllSelection = () => {
        if (!selectedCot) return;
        const validItens = selectedCot.itens.filter(it => it.PrecoOfertado);
        if (selectedItems.length === validItens.length) {
            setSelectedItems([]);
        } else {
            setSelectedItems(validItens.map(it => it.ID));
        }
    };

    const copyLink = (token, senha) => {
        const link = `${window.location.origin}/public-quotation/${token}`;
        navigator.clipboard.writeText(`Link: ${link}\nSenha: ${senha}`);
        alert('Link e Senha copiados para a área de transferência!');
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Excluir esta cotação permanentemente?')) return;
        try {
            const res = await fetch(`/api/cotacoes/${id}`, { method: 'DELETE', credentials: 'include' });
            if (res.ok) {
                fetchQuotations();
                setSelectedCot(null);
            }
        } catch (error) {
            console.error('Erro ao excluir:', error);
        }
    };

    const openEditModal = () => {
        setIsEditModalOpen(true);
    };

    const handleNewQuotationClick = () => {
        setIsSupplierSelectorOpen(true);
    };

    const handleSupplierSelect = (supplier) => {
        setSelectedSupplierForNew(supplier);
        setIsSupplierSelectorOpen(false);
        setIsEditModalOpen(true);
    };

    const handleFormSuccess = () => {
        setIsEditModalOpen(false);
        setSelectedSupplierForNew(null);
        fetchQuotations();
        if (selectedCot) fetchDetails(selectedCot.ID);
    };

    const closeFormModal = () => {
        setIsEditModalOpen(false);
        setSelectedSupplierForNew(null);
    };

    return (
        <div className="gestao-cotacoes-container">
            <Navbar user={user} />
            <div className="cot-layout">
                <aside className="cot-sidebar">
                    <div className="sidebar-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px' }}>
                        <h3>Cotações Ativas</h3>
                        <button className="btn-classic primary" onClick={handleNewQuotationClick}>+ Nova</button>
                    </div>
                    <div className="cot-list">
                        {loading ? <p style={{ padding: '10px' }}>Carregando...</p> :
                            quotations.length === 0 ? <p style={{ padding: '10px' }}>Nenhuma cotação encontrada.</p> :
                                quotations.map(cot => (
                                    <div
                                        key={cot.ID}
                                        className={`cot-item ${selectedCot?.ID === cot.ID ? 'active' : ''}`}
                                        onClick={() => fetchDetails(cot.ID)}
                                    >
                                        <div className="cot-item-main">
                                            <strong>{cot.NomeFornecedor}</strong>
                                            <span className={`status-badge ${cot.Status.toLowerCase()}`}>{cot.Status}</span>
                                        </div>
                                        <div className="cot-item-sub">
                                            <span>
                                                #{cot.ID} - {cot.Descricao || '(Sem nome)'}
                                                {cot.idPedidoOrigem && ` (Orçamento #${cot.idPedidoOrigem})`}
                                                {` - ${formatDate(cot.DataCotacao)}`}
                                            </span>
                                            <span>Expira: {formatDate(cot.DataExpiracao)}</span>
                                        </div>
                                    </div>
                                ))}
                    </div>
                </aside>

                <main className="cot-content">
                    {selectedCot ? (
                        <div className="cot-detail-window">
                            <div className="win-title">
                                Detalhes: #{selectedCot.ID}
                                {selectedCot.Descricao && ` - ${selectedCot.Descricao}`}
                                {selectedCot.idPedidoOrigem && ` (Orçamento #${selectedCot.idPedidoOrigem})`}
                                {` - ${selectedCot.Status || selectedCot.status}`}
                            </div>
                            <div className="win-body">
                                <div className="detail-actions">
                                    {((selectedCot.Status || selectedCot.status)?.toLowerCase() === 'rascunho' ||
                                        (selectedCot.Status || selectedCot.status)?.toLowerCase() === 'pendente') && (
                                            <button className="btn-classic primary" onClick={openEditModal}>✏️ Editar Cotação</button>
                                        )}
                                    {(selectedCot.Status || selectedCot.status)?.toLowerCase() === 'enviada' && (
                                        <button className="btn-classic primary" onClick={async () => {
                                            // Directly update status to Verificada
                                            const res = await fetch(`/api/cotacoes/${selectedCot.ID}`, {
                                                method: 'PUT',
                                                headers: { 'Content-Type': 'application/json' },
                                                body: JSON.stringify({ status: 'Verificada' }),
                                                credentials: 'include'
                                            });
                                            if (res.ok) {
                                                alert('Cotação marcada como Verificada!');
                                                fetchQuotations();
                                                fetchDetails(selectedCot.ID);
                                            }
                                        }}>✅ Marcar como Verificada</button>
                                    )}
                                    <button className="btn-classic" onClick={() => copyLink(selectedCot.Token, selectedCot.Senha)}>Copiar Link de Acesso</button>
                                    <button className="btn-classic danger" onClick={() => handleDelete(selectedCot.ID)}>Excluir Cotação</button>

                                    {selectedItems.length > 0 && (
                                        <div className="bulk-actions-toolbar">
                                            <span>{selectedItems.length} selecionados:</span>
                                            <button className="btn-classic primary" onClick={() => handleSendToOrder()}>
                                                📝 Vincular no Orçamento
                                            </button>
                                            <button className="btn-classic" onClick={() => setSelectedItems([])}>Desmarcar</button>
                                        </div>
                                    )}
                                </div>

                                <div className="info-grid">
                                    <div className="info-block">
                                        <strong>Fornecedor:</strong>
                                        <p>{quotations.find(q => q.ID === selectedCot.ID)?.NomeFornecedor}</p>
                                    </div>
                                    <div className="info-block">
                                         <strong>Data da Solicitação:</strong>
                                         <p>{formatDate(selectedCot.DataCotacao)}</p>
                                    </div>
                                    <div className="info-block">
                                        <strong>Senha de Acesso:</strong>
                                        <p><code>{selectedCot.Senha}</code></p>
                                    </div>
                                    {selectedCot.idPedidoOrigem && (
                                        <div className="info-block">
                                            <strong>Orçamento Vinculado:</strong>
                                            <p>#{selectedCot.idPedidoOrigem}</p>
                                        </div>
                                    )}
                                </div>

                                <div className="obs-section">
                                    <strong>Obs do Fornecedor:</strong>
                                    <p className="obs-content">{selectedCot.ObservacaoFornecedor || '(Nenhuma observação enviada)'}</p>
                                </div>

                                <div className="items-table-container">
                                    <table className="items-table">
                                        <thead>
                                            <tr>
                                                <th style={{ width: '40px', textAlign: 'center' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedCot.itens.filter(it => it.PrecoOfertado).length > 0 && selectedItems.length === selectedCot.itens.filter(it => it.PrecoOfertado).length}
                                                        onChange={toggleAllSelection}
                                                    />
                                                </th>
                                                <th>Produto</th>
                                                <th>Qtd</th>
                                                <th>Preço Cotado</th>
                                                <th>Ações</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {selectedCot.itens.map(item => (
                                                <tr key={item.ID} className={selectedItems.includes(item.ID) ? 'selected-row' : ''}>
                                                    <td style={{ textAlign: 'center' }}>
                                                        {item.PrecoOfertado ? (
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedItems.includes(item.ID)}
                                                                onChange={() => toggleItemSelection(item.ID)}
                                                            />
                                                        ) : null}
                                                    </td>
                                                    <td>{item.NomeProduto}</td>
                                                    <td style={{ textAlign: 'center' }}>{item.Quantidade}</td>
                                                    <td className="price-td">
                                                        {item.PrecoOfertado ? formatCurrency(item.PrecoOfertado) : <span className="pending">Pendente</span>}
                                                    </td>
                                                    <td>
                                                        <div className="item-actions-grid" style={{ display: 'flex', gap: '5px' }}>
                                                            {item.PrecoOfertado && (
                                                                <>
                                                                    <button
                                                                        className="btn-tiny"
                                                                        onClick={() => handleSyncClick(item)}
                                                                        title="Sincronizar Produto"
                                                                    >
                                                                        📦 Sincronizar
                                                                    </button>
                                                                    <button
                                                                        className="btn-tiny"
                                                                        onClick={() => handleConvertToProduct(item)}
                                                                        title="Converter/Atualizar Produto"
                                                                    >
                                                                        🏷️ Converter
                                                                    </button>
                                                                    <button
                                                                        className="btn-tiny primary"
                                                                        onClick={() => handleSendToOrder(item)}
                                                                        title="Enviar para Orçamento"
                                                                    >
                                                                        📝 Orçamento
                                                                    </button>
                                                                </>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="cot-empty">
                            <p>Selecione uma cotação para ver os detalhes e preços enviados.</p>
                        </div>
                    )}
                </main>
            </div>

            {isEditModalOpen && (
                <QuotationFormModal
                    supplier={selectedSupplierForNew}
                    quotationId={selectedSupplierForNew ? null : selectedCot?.ID}
                    onClose={closeFormModal}
                    onSuccess={handleFormSuccess}
                />
            )}

            {isOrderSelectorOpen && (
                <OrderSelectorModal
                    title="Selecionar Cliente e Orçamento"
                    onClose={() => setIsOrderSelectorOpen(false)}
                    onSelect={(order) => {
                        if (isLinkingQuotation) {
                            confirmSendToOrder(order);
                        }
                        setIsOrderSelectorOpen(false);
                    }}
                />
            )}

            {isProductSelectorOpen && (
                <ProductSelectorModal
                    onClose={() => setIsProductSelectorOpen(false)}
                    onSelect={confirmSync}
                />
            )}
            {isSupplierSelectorOpen && (
                <SupplierSelectorModal
                    onClose={() => setIsSupplierSelectorOpen(false)}
                    onSelect={handleSupplierSelect}
                />
            )}
        </div>
    );
};

export default GestaoCotacoes;
